import { useNavigate } from "react-router-dom";
import { Icon, Swatch, type SwatchKind } from "@/components/primitives";
import { MobileFrame } from "@/components/MobileFrame";
import { useAuth } from "@/hooks/useAuth";
import { useTopCollectors } from "@/hooks/useTopCollectors";

// Full leaderboard. Ranks every collector by number of posted scans in the
// last 7 days. Highlights the current viewer's row when they appear.
export default function Leaders() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { collectors, loading } = useTopCollectors(20, 7);

  const goProfile = (handle: string, isYou: boolean) =>
    isYou ? nav("/you") : nav(`/profile/${encodeURIComponent("@" + handle)}`);

  return (
    <MobileFrame>
      <div className="paper-bg" style={{ minHeight: "100vh", paddingBottom: 80 }}>
        <div style={{ padding: "12px 20px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button onClick={() => nav(-1)} style={{ background: "none", border: "none", padding: 6, cursor: "pointer", color: "hsl(var(--ink))" }}>
            {Icon.chev(18, "l")}
          </button>
          <span className="caps">Last 7 days</span>
          <div style={{ width: 30 }} />
        </div>
        <div style={{ padding: "10px 20px 0" }}>
          <span className="caps" style={{ color: "hsl(var(--rust))" }}>Ranked & rendered</span>
          <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 900, fontSize: 48, color: "hsl(var(--ink))", letterSpacing: "-0.03em", lineHeight: 0.9, marginTop: 4 }}>
            The Board
          </div>
          <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 14, color: "hsl(var(--ink-2))", marginTop: 6 }}>
            ranked by posted finds this week
          </div>
        </div>

        <div style={{ padding: "18px 20px 0" }}>
          <div style={{ background: "hsl(var(--cream))", border: "1px solid hsl(var(--ink))" }}>
            {loading && (
              <div style={{ padding: "20px", textAlign: "center", fontFamily: "'Fraunces', 'Playfair Display', serif", color: "hsl(var(--ink-2))" }}>
                tallying the board…
              </div>
            )}
            {!loading && collectors.length === 0 && (
              <div style={{ padding: "20px", textAlign: "center" }}>
                <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 15, color: "hsl(var(--ink-2))" }}>
                  Nobody's posted this week — yet.
                </div>
                <button onClick={() => nav("/scan")} className="caps" style={{
                  background: "none", border: "none", marginTop: 8, cursor: "pointer",
                  textDecoration: "underline", color: "hsl(var(--ink))",
                }}>
                  Be the first → scan something
                </button>
              </div>
            )}
            {collectors.map((c, i, a) => {
              const isYou = !!user && c.user_id === user.id;
              return (
                <button
                  key={c.user_id}
                  onClick={() => goProfile(c.handle, isYou)}
                  style={{
                    display: "flex", gap: 12, padding: "12px 14px", width: "100%",
                    borderBottom: i < a.length - 1 ? "1px dashed hsl(var(--sand-deep))" : "none",
                    background: isYou ? "hsl(var(--paper-2))" : "transparent",
                    alignItems: "center", cursor: "pointer", border: "none", borderRadius: 0, textAlign: "left",
                  }}
                >
                  <div style={{
                    fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 900, fontSize: 28,
                    color: i === 0 ? "hsl(var(--rust))" : "hsl(var(--ink))",
                    width: 32, textAlign: "center", lineHeight: 1,
                  }}>
                    {c.rank}
                  </div>
                  <Swatch
                    kind={(c.avatar as SwatchKind) || "olive"}
                    style={{ width: 32, height: 32, borderRadius: "50%", border: "1px solid hsl(var(--ink))", flexShrink: 0 }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                      <span style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 16, color: "hsl(var(--ink))" }}>
                        @{c.handle}
                        {isYou && <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: "hsl(var(--rust))", marginLeft: 6 }}>· that's you</span>}
                      </span>
                      <span style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 18, color: "hsl(var(--ink))" }}>{c.score}</span>
                    </div>
                    <div className="caps" style={{ marginTop: 3, color: "hsl(var(--rust))" }}>
                      {c.score} {c.score === 1 ? "find" : "finds"} posted
                    </div>
                    {c.item && (
                      <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 13, color: "hsl(var(--ink-2))", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        latest: {c.item}
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </MobileFrame>
  );
}
